import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import { saveBranding } from '../services/brandingService';
import OrganisationMark from '../components/OrganisationMark';

export default function BrandingSettings() {
  const { userData } = useAuth();
  const { branding } = useBranding();
  const [form, setForm] = useState(branding);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const role = String(userData?.role || '').toLowerCase();
  useEffect(() => setForm(branding), [branding]);

  if (role !== 'admin') {
    return <div className="ui-alert ui-alert-danger">Only Admin may access Branding Settings.</div>;
  }

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      await saveBranding(form, userData?.id);
      setMessage('Branding settings saved.');
    } catch (error) {
      setMessage(error.message || 'Unable to save branding settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ui-page max-w-5xl">
      <div className="ui-page-header">
        <div>
          <p className="ui-eyebrow">Organisation settings</p>
          <h1 className="ui-page-title">Branding & Appearance</h1>
          <p className="ui-page-subtitle">Pravah remains the fixed product brand. Your organisation identity appears alongside it.</p>
        </div>
      </div>

      <form onSubmit={handleSave} className="grid gap-6 lg:grid-cols-[1.2fr_.8fr]">
        <section className="ui-card p-6 space-y-5">
          <div>
            <h2 className="ui-section-title">Organisation identity</h2>
            <p className="ui-section-help">These text details appear on the login page and in the application header.</p>
          </div>

          <label className="ui-field">
            <span>Organisation name</span>
            <input className="ui-input" value={form.organisationName || ''} onChange={(e) => update('organisationName', e.target.value)} required />
          </label>
          <label className="ui-field">
            <span>Organisation tagline <small>(optional)</small></span>
            <input className="ui-input" value={form.organisationTagline || ''} onChange={(e) => update('organisationTagline', e.target.value)} />
          </label>
          <label className="ui-field">
            <span>Login heading</span>
            <input className="ui-input" value={form.loginHeading || ''} onChange={(e) => update('loginHeading', e.target.value)} />
          </label>
          <label className="ui-field">
            <span>Login subheading</span>
            <textarea className="ui-input min-h-[92px]" value={form.loginSubheading || ''} onChange={(e) => update('loginSubheading', e.target.value)} />
          </label>
          <label className="ui-field">
            <span>Support email <small>(optional)</small></span>
            <input type="email" className="ui-input" value={form.supportEmail || ''} onChange={(e) => update('supportEmail', e.target.value)} />
          </label>
          <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <input type="checkbox" className="mt-1 h-4 w-4" checked={form.showOrganisationLogo !== false} onChange={(e) => update('showOrganisationLogo', e.target.checked)} />
            <span>
              <strong className="block text-sm text-slate-800">Show organisation logo</strong>
              <small className="mt-1 block text-slate-500">The logo is loaded from <code>public/brand/organisation-logo.png</code>.</small>
            </span>
          </label>

          <div className="ui-note">
            Logo upload is intentionally disabled in this version to avoid Firebase Storage and billing. Replace the local file and redeploy whenever the organisation logo changes.
          </div>

          {message && <div className={message.includes('saved') ? 'ui-alert ui-alert-success' : 'ui-alert ui-alert-danger'}>{message}</div>}
          <div className="flex justify-end">
            <button disabled={saving} className="ui-btn ui-btn-primary" type="submit">{saving ? 'Saving…' : 'Save branding'}</button>
          </div>
        </section>

        <aside className="space-y-6">
          <section className="ui-card overflow-hidden">
            <div className="bg-[#0B1F33] p-6 text-center">
              <img src="/brand/pravah-wordmark.png" alt="Pravah" className="mx-auto max-h-24 max-w-full object-contain" />
              <p className="mt-3 text-sm text-[#F7F3E8]/80">Your Practice. In Flow. With Pravah.</p>
            </div>
            <div className="p-6 text-center">
              {form.showOrganisationLogo !== false && (
                <OrganisationMark
                  branding={form}
                  className="mx-auto mb-3 max-h-16 max-w-[180px] object-contain"
                  fallbackClassName="mx-auto mb-3 grid h-14 w-14 place-items-center rounded-xl bg-slate-100 text-xl font-bold text-slate-500"
                />
              )}
              <p className="font-semibold text-slate-900">{form.organisationName || 'Your Organisation'}</p>
              {form.organisationTagline && <p className="mt-1 text-sm text-slate-500">{form.organisationTagline}</p>}
            </div>
          </section>
          <div className="ui-note">The Pravah wordmark, symbol and favicon remain fixed. The organisation logo is optional and is maintained as a local React asset.</div>
        </aside>
      </form>
    </div>
  );
}
